import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaVideo, FaVideoSlash, FaMicrophone, FaMicrophoneSlash,
  FaTimes, FaHeart, FaCog, FaExchangeAlt,
  FaPaperPlane, FaEye, FaClock, FaThumbtack
} from 'react-icons/fa';

const formatDuration = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};

const MILESTONE_MESSAGES = {
  10: 'First 10 viewers!', 25: '25 viewers!', 50: 'You hit 50 viewers!',
  100: '100 viewers! You\'re on fire!', 250: '250 viewers! Amazing!',
  500: '500 viewers! Incredible!', 1000: '1K viewers! Legendary!',
};

const LiveBroadcastView = ({
  stream, isVideoOn, setIsVideoOn, isAudioOn, setIsAudioOn,
  duration, viewerCount, likes, milestone, showHearts,
  comments, newComment, setNewComment, pinnedComment,
  handleEndLive, handleSendComment, handlePinComment, handleUnpinComment,
  commentsEndRef, selectedCat,
}) => (
  <div className="fixed inset-0 bg-[#0B0F14] z-50" data-testid="live-broadcast-screen">
    <div className="relative h-full">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#23cda0]/15 via-[#0B0F14] to-[#6c30ad]/10" />
        {isVideoOn && (
          <motion.div animate={{ opacity: [0.2, 0.4, 0.2] }} transition={{ duration: 6, repeat: Infinity }} className="absolute inset-0 bg-gradient-to-t from-[#0B0F14] via-transparent to-transparent" />
        )}
      </div>

      {/* Mock Video Center */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {isVideoOn ? (
          <div className="text-center">
            <motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 3, repeat: Infinity }} className="w-16 h-16 sm:w-28 sm:h-28 rounded-3xl bg-gradient-to-br from-[#23cda0] via-[#2263f2] to-[#6c30ad] flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-2xl shadow-[#2263f2]/30">
              <FaVideo className="text-white text-xl sm:text-4xl" />
            </motion.div>
            <p className="text-white/30 text-sm font-medium">Broadcasting Live</p>
          </div>
        ) : (
          <FaVideoSlash className="text-white/15 text-3xl sm:text-6xl" />
        )}
      </div>

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/50 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent pointer-events-none" />

      {/* TOP BAR */}
      <div className="absolute top-0 left-0 right-0 p-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 bg-red-500/90 backdrop-blur-sm px-3 py-1.5 rounded-lg" data-testid="live-badge">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span className="text-white font-bold text-xs tracking-wider">LIVE</span>
            </div>
            <div className="flex items-center space-x-1.5 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg" data-testid="stream-duration">
              <FaClock className="text-white/60" size={10} />
              <span className="text-white/80 text-xs font-mono font-medium">{formatDuration(duration)}</span>
            </div>
            <div className="flex items-center space-x-1.5 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg" data-testid="viewer-count">
              <FaEye className="text-[#23cda0]" size={10} />
              <span className="text-white/80 text-xs font-medium">{viewerCount}</span>
            </div>
            <div className="flex items-center space-x-1.5 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg">
              <FaHeart className="text-red-400" size={10} />
              <span className="text-white/80 text-xs font-medium">{likes}</span>
            </div>
          </div>
          <button onClick={handleEndLive} data-testid="end-live-btn" className="bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-all hover:scale-105 active:scale-95">End Stream</button>
        </div>
        <div className="mt-3 bg-black/30 backdrop-blur-sm rounded-lg px-4 py-2.5 max-w-md border border-white/5">
          <div className="flex items-center space-x-2 mb-0.5">
            {selectedCat && <selectedCat.icon size={10} style={{ color: selectedCat.color }} />}
            <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: selectedCat?.color || '#23cda0' }}>{selectedCat?.label}</span>
          </div>
          <h3 className="text-white font-bold text-sm">{stream?.title}</h3>
          {stream?.description && <p className="text-white/50 text-xs mt-0.5">{stream.description}</p>}
        </div>
      </div>

      {/* MILESTONE CELEBRATION */}
      <AnimatePresence>
        {milestone && (
          <motion.div key={`milestone-${milestone}`} initial={{ opacity: 0, scale: 0.5, y: 50 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.8, y: -30 }} transition={{ type: 'spring', damping: 15 }} className="absolute top-1/3 left-1/2 -translate-x-1/2 z-30 pointer-events-none" data-testid="milestone-banner">
            <div className="bg-gradient-to-r from-[#23cda0] via-[#2263f2] to-[#6c30ad] rounded-2xl px-8 py-5 shadow-2xl shadow-[#2263f2]/40 text-center">
              <motion.div animate={{ rotate: [0, -10, 10, -10, 0] }} transition={{ duration: 0.5, delay: 0.3 }} className="text-4xl mb-2">
                {milestone >= 500 ? '🏆' : milestone >= 100 ? '🔥' : '🎉'}
              </motion.div>
              <p className="text-white font-bold text-xl">{MILESTONE_MESSAGES[milestone]}</p>
              <p className="text-white/70 text-sm mt-1">{viewerCount} people watching</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FLOATING HEARTS */}
      <AnimatePresence>
        {showHearts.map((heartId) => (
          <motion.div key={heartId} initial={{ opacity: 1, y: 0, x: 0, scale: 1 }} animate={{ opacity: 0, y: -180, x: (Math.random() - 0.5) * 80, scale: 0.5 }} exit={{ opacity: 0 }} transition={{ duration: 2.5, ease: 'easeOut' }} className="absolute bottom-36 right-16 text-2xl pointer-events-none" style={{ color: ['#23cda0', '#47aef0', '#2263f2', '#6c30ad'][Math.floor(Math.random() * 4)] }}>
            <FaHeart />
          </motion.div>
        ))}
      </AnimatePresence>

      {/* BOTTOM: COMMENTS + CONTROLS */}
      <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
        {pinnedComment && (
          <div className="max-w-lg mb-3" data-testid="pinned-comment-banner">
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-start space-x-2 bg-[#23cda0]/15 backdrop-blur-xl rounded-xl px-3 py-2.5 border border-[#23cda0]/30">
              <FaThumbtack className="text-[#23cda0] mt-0.5 flex-shrink-0" size={12} />
              <div className="flex-1 min-w-0">
                <span className="text-[#23cda0] font-bold text-xs">{pinnedComment.username}</span>
                <p className="text-white/90 text-sm leading-snug">{pinnedComment.content}</p>
              </div>
              <button onClick={handleUnpinComment} data-testid="unpin-comment-btn" className="text-white/30 hover:text-white/70 p-1 flex-shrink-0 transition-colors"><FaTimes size={10} /></button>
            </motion.div>
          </div>
        )}

        <div className="max-w-lg h-52 overflow-y-auto mb-4 scrollbar-hide" data-testid="live-comments">
          <div className="space-y-1.5">
            {comments.length === 0 && <p className="text-white/20 text-sm py-6 text-center">Waiting for comments...</p>}
            {comments.map((comment) => (
              <motion.div key={comment.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-start space-x-2 bg-black/30 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/5 group">
                <img src={comment.profile_picture || `https://ui-avatars.com/api/?name=${comment.username}&background=161B22&color=23cda0`} alt="" className="w-7 h-7 rounded-full flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-[#23cda0] font-bold text-xs">{comment.username}</span>
                  <p className="text-white/80 text-sm leading-snug">{comment.content}</p>
                </div>
                {pinnedComment?.id !== comment.id && (
                  <button onClick={() => handlePinComment(comment.id)} data-testid={`pin-comment-${comment.id}`} className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-[#23cda0] p-1 flex-shrink-0 transition-all" title="Pin comment"><FaThumbtack size={11} /></button>
                )}
              </motion.div>
            ))}
            <div ref={commentsEndRef} />
          </div>
        </div>

        <div className="flex items-center space-x-3 max-w-lg">
          <form onSubmit={handleSendComment} className="flex-1 flex items-center space-x-2">
            <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Say something..." className="flex-1 bg-white/10 backdrop-blur-sm text-white placeholder-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#23cda0]/50 border border-white/10" data-testid="live-comment-input" />
            <button type="submit" disabled={!newComment.trim()} className="bg-gradient-to-r from-[#23cda0] to-[#2263f2] text-white p-2.5 rounded-xl hover:from-[#1db890] hover:to-[#1b56d9] transition-all disabled:opacity-30 disabled:cursor-not-allowed" data-testid="send-live-comment"><FaPaperPlane size={14} /></button>
          </form>
        </div>

        <div className="flex items-center justify-center space-x-3 mt-4">
          <button onClick={() => setIsVideoOn(!isVideoOn)} data-testid="live-toggle-video" className={`p-3.5 rounded-xl transition-all ${isVideoOn ? 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white' : 'bg-red-500/90 text-white'}`}>{isVideoOn ? <FaVideo size={18} /> : <FaVideoSlash size={18} />}</button>
          <button onClick={() => setIsAudioOn(!isAudioOn)} data-testid="live-toggle-audio" className={`p-3.5 rounded-xl transition-all ${isAudioOn ? 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white' : 'bg-red-500/90 text-white'}`}>{isAudioOn ? <FaMicrophone size={18} /> : <FaMicrophoneSlash size={18} />}</button>
          <button className="p-3.5 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white transition-all"><FaExchangeAlt size={18} /></button>
          <button className="p-3.5 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white transition-all relative" data-testid="live-like-btn">
            <FaHeart className="text-red-400" size={18} />
            {likes > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{likes > 99 ? '99+' : likes}</span>}
          </button>
          <button className="p-3.5 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white transition-all"><FaCog size={18} /></button>
        </div>
      </div>
    </div>
  </div>
);

export default LiveBroadcastView;
